import React, { useCallback, useEffect, useState, useRef } from 'react';

import {
  DisplayLine,
  EditUserButton,
  UsernameDisplay,
  UsernameRawDisplay,
  UsernameRawInput,
} from './styles';

export interface UserInputDisplayProps {
  username: string;
  onUserNameChanged: (username: string) => void;
}

const UserInputDisplay = ({
  username,
  onUserNameChanged,
}: UserInputDisplayProps) => {
  const [internalUserName, setInternalUserName] = useState('');
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);

  const setUserName = useCallback(() => {
    setIsEditingUsername(false);
    onUserNameChanged(internalUserName);
  }, [internalUserName, onUserNameChanged]);

  useEffect(() => {
    setInternalUserName(username);
  }, [username]);

  useEffect(() => {
    if (isEditingUsername && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditingUsername]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        editButtonRef.current &&
        !editButtonRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setUserName();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setUserName]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setUserName();
    }
  };

  return (
    <UsernameDisplay $editable={isEditingUsername}>
      {isEditingUsername ? (
        <DisplayLine>
          <UsernameRawInput
            ref={inputRef}
            type="text"
            value={internalUserName}
            onChange={(e) => setInternalUserName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Nome de Usuário"
          />
          <EditUserButton
            ref={editButtonRef}
            icon="log-in"
            onClick={() => setUserName()}
          />
        </DisplayLine>
      ) : (
        <DisplayLine>
          <UsernameRawDisplay>
            {internalUserName || 'Entre com o nome de Usuário'}
          </UsernameRawDisplay>
          <EditUserButton
            ref={editButtonRef}
            icon="edit"
            onClick={() => {
              setIsEditingUsername(true);
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }}
          />
        </DisplayLine>
      )}
    </UsernameDisplay>
  );
};

export default UserInputDisplay;
